import { Chip, Progress } from '@heroui/react';
import React, { FC, useState } from 'react';

import { StockPhoto, useGetStockUsage } from '../../../../../queries';
import { useAdsGeneratorStore } from '../../../model/store';
import { PanelSection } from '../../common';
import { StockImportModal } from '../StockImportModal';
import { StockSearchGrid } from '../StockSearchGrid';

/**
 * Podpowiedzi wyszukiwania pod nasze kreacje: reklama Hotelspota mówi do
 * hotelarza, więc użyteczne są wnętrza obiektu i praca personelu, a nie
 * wakacyjne krajobrazy. Frazy są po angielsku, bo tak opisana jest biblioteka
 * Magnifica; etykiety zostają po polsku.
 */
const SUGGESTED_QUERIES: { label: string; term: string }[] = [
  { label: 'Lobby i recepcja', term: 'hotel reception desk lobby' },
  { label: 'Pokój hotelowy', term: 'modern hotel room interior' },
  { label: 'Gość ze smartfonem', term: 'guest using smartphone hotel room' },
  { label: 'Śniadanie i restauracja', term: 'hotel breakfast restaurant buffet' },
  { label: 'Spa i wellness', term: 'hotel spa wellness relax' },
  { label: 'Praca recepcji', term: 'hotel receptionist working front desk' },
];

/**
 * Bank zdjęć Magnific.
 *
 * Wyszukiwanie jest darmowe i pokazuje wyłącznie podglądy z CDN Magnifica.
 * Nic się tu nie zapisuje: do biblioteki i na planszę trafia dopiero zdjęcie
 * potwierdzone w oknie importu, bo dopiero ono zabiera jedno z dziennych
 * pobrań. Dlatego kliknięcie kafelka otwiera potwierdzenie, a nie pobiera.
 */
export const StockPanel: FC = () => {
  const scene = useAdsGeneratorStore((state) => state.scene);
  const { data: usage } = useGetStockUsage();

  // Podpowiedź przestawia wyszukiwarkę przez klucz komponentu: pole tekstowe
  // trzyma własny stan, a my chcemy je tylko zasiać nową frazą.
  const [query, setQuery] = useState(SUGGESTED_QUERIES[0].term);
  const [selected, setSelected] = useState<StockPhoto | null>(null);

  if (!scene) return null;

  return (
    <>
      <PanelSection title="Bank zdjęć Magnific">
        {usage && (
          <Progress
            size="sm"
            radius="sm"
            aria-label="Zużycie dziennej puli pobrań"
            label={`Pobrania dzisiaj: ${usage.used} ze ${usage.limit}`}
            value={usage.used}
            maxValue={usage.limit}
            color={usage.used >= usage.warnThreshold ? 'warning' : 'primary'}
            classNames={{ label: 'text-xs text-gray-500' }}
          />
        )}

        <StockSearchGrid key={query} initialTerm={query} onPick={setSelected} />

        <div className="flex flex-wrap gap-1">
          {SUGGESTED_QUERIES.map((suggestion) => (
            <Chip
              key={suggestion.term}
              size="sm"
              radius="sm"
              variant={query === suggestion.term ? 'solid' : 'flat'}
              className="cursor-pointer text-xs"
              onClick={() => setQuery(suggestion.term)}
            >
              {suggestion.label}
            </Chip>
          ))}
        </div>

        <p className="text-xs text-gray-500 leading-relaxed">
          Przeglądanie nic nie kosztuje. Pulę zabiera dopiero pobranie wybranego zdjęcia.
        </p>
      </PanelSection>

      {selected && (
        <StockImportModal
          photo={selected}
          downloadsLeft={usage?.left ?? 0}
          onClose={() => setSelected(null)}
        />
      )}
    </>
  );
};
